import { z } from 'zod';
import { CommsChannel } from '../enums.js';
import { IsoDateTime, NonEmptyName, Pence, Slug, Uuid } from '../primitives.js';

/**
 * Comms send-pipeline contract (Phase 3). NEW file — the existing entities/comms.ts (allowance /
 * policy / CommsLogEntry) is untouched. Reuses CommsChannel from enums.js (do not redeclare).
 * Entity/response schemas carry `.meta({ id })`; query schemas must NOT.
 */

// ── Enums ────────────────────────────────────────────────────────────────────
export const CommsKind = z.enum(['reminder', 'campaign', 'gap_offer', 'transactional', 'manual']);
export type CommsKind = z.infer<typeof CommsKind>;

export const CommsMessageStatus = z.enum([
  'queued',
  'sent',
  'delivered',
  'failed',
  'paused_at_cap',
  'skipped_no_consent',
  'skipped_no_contact',
]);
export type CommsMessageStatus = z.infer<typeof CommsMessageStatus>;

export const SmsEncoding = z.enum(['gsm7', 'ucs2']);
export type SmsEncoding = z.infer<typeof SmsEncoding>;

const ReminderStatus = z.enum(['pending', 'sent', 'cancelled', 'failed']);
const CampaignStatus = z.enum([
  'draft',
  'scheduled',
  'sending',
  'sent',
  'paused_at_cap',
  'cancelled',
]);

// ── Message audit log ──────────────────────────────────────────────────────────
export const CommsMessage = z
  .object({
    id: Uuid,
    salonId: Uuid,
    renterId: Uuid.nullable(),
    clientId: Uuid.nullable(),
    appointmentId: Uuid.nullable(),
    campaignId: Uuid.nullable(),
    kind: CommsKind,
    channel: CommsChannel,
    requestedChannel: CommsChannel,
    routedFromChannel: CommsChannel.nullable(),
    toRedacted: z.string(),
    subject: z.string().nullable(),
    segments: z.int().min(0),
    encoding: SmsEncoding.nullable(),
    costPence: Pence,
    smartRoutingSavedPence: Pence,
    metered: z.boolean(),
    status: CommsMessageStatus,
    providerMessageId: z.string().nullable(),
    sentAt: IsoDateTime.nullable(),
    createdAt: IsoDateTime,
  })
  .meta({ id: 'CommsMessage' });
export type CommsMessage = z.infer<typeof CommsMessage>;

export const SendMessageRequest = z
  .object({
    channel: CommsChannel,
    clientId: Uuid.nullish(),
    appointmentId: Uuid.nullish(),
    to: z.string().nullish().describe('explicit recipient overriding the client contact'),
    templateId: Uuid.nullish(),
    subject: z.string().max(200).nullish(),
    body: z.string().min(1).max(2000),
    kind: CommsKind.default('manual'),
    allowSmartRouting: z.boolean().default(true),
    idempotencyKey: z.string().min(8).max(200).optional(),
  })
  .meta({ id: 'SendMessageRequest' });
export type SendMessageRequest = z.infer<typeof SendMessageRequest>;

export const SendMessageResult = z
  .object({
    message: CommsMessage,
    delivered: z.boolean(),
    pausedAtCap: z.boolean(),
    routedFromChannel: CommsChannel.nullable(),
    savedPence: Pence,
  })
  .meta({ id: 'SendMessageResult' });
export type SendMessageResult = z.infer<typeof SendMessageResult>;

// ── Reminders ──────────────────────────────────────────────────────────────────
export const ReminderSchedule = z
  .object({
    id: Uuid,
    salonId: Uuid,
    renterId: Uuid.nullable(),
    appointmentId: Uuid,
    clientId: Uuid.nullable(),
    leadMin: z.int().min(0).max(20160),
    preferredChannel: CommsChannel,
    templateId: Uuid.nullable(),
    fireAt: IsoDateTime,
    status: ReminderStatus,
    commsMessageId: Uuid.nullable(),
    createdAt: IsoDateTime,
  })
  .meta({ id: 'ReminderSchedule' });
export type ReminderSchedule = z.infer<typeof ReminderSchedule>;

export const ScheduleRemindersRequest = z
  .object({
    appointmentId: Uuid,
    leadsMin: z.array(z.int().min(0).max(20160)).min(1).max(5),
    preferredChannel: CommsChannel.default('sms'),
    templateId: Uuid.nullish(),
  })
  .meta({ id: 'ScheduleRemindersRequest' });
export type ScheduleRemindersRequest = z.infer<typeof ScheduleRemindersRequest>;

// ── Campaigns ──────────────────────────────────────────────────────────────────
export const CampaignAudienceFilter = z
  .object({
    notSeenForDays: z.int().min(0).max(3650).optional(),
    serviceCategory: z.string().optional(),
    minVisitCount: z.int().min(0).optional(),
    minLifetimeValuePence: Pence.optional(),
    renterId: Uuid.nullish().describe("restrict to a renter's own book"),
  })
  .meta({ id: 'CampaignAudienceFilter' });
export type CampaignAudienceFilter = z.infer<typeof CampaignAudienceFilter>;

export const Campaign = z
  .object({
    id: Uuid,
    salonId: Uuid,
    name: NonEmptyName,
    channel: CommsChannel,
    templateId: Uuid.nullable(),
    subject: z.string().nullable(),
    body: z.string(),
    audienceFilter: CampaignAudienceFilter,
    requiresMarketingConsent: z.boolean(),
    status: CampaignStatus,
    scheduledAt: IsoDateTime.nullable(),
    recipientCount: z.int().min(0),
    sentCount: z.int().min(0),
    skippedCount: z.int().min(0),
    costPence: Pence,
    createdAt: IsoDateTime,
  })
  .meta({ id: 'Campaign' });
export type Campaign = z.infer<typeof Campaign>;

export const CreateCampaign = z
  .object({
    name: NonEmptyName,
    channel: CommsChannel.default('email'),
    templateId: Uuid.nullish(),
    subject: z.string().max(200).nullish(),
    body: z.string().min(1).max(4000),
    audienceFilter: CampaignAudienceFilter.default({}),
    requiresMarketingConsent: z.boolean().default(true),
    scheduledAt: IsoDateTime.nullish(),
    idempotencyKey: z.string().min(8).max(200).optional(),
  })
  .meta({ id: 'CreateCampaign' });
export type CreateCampaign = z.infer<typeof CreateCampaign>;

export const CampaignPreview = z
  .object({
    recipientCount: z.int().min(0),
    consentSkippedCount: z.int().min(0),
    noContactSkippedCount: z.int().min(0),
    estimatedCostPence: Pence,
    estimatedSegments: z.int().min(0),
    wouldPauseAtCap: z.boolean(),
  })
  .meta({ id: 'CampaignPreview' });
export type CampaignPreview = z.infer<typeof CampaignPreview>;

// ── Templates ──────────────────────────────────────────────────────────────────
export const CommsTemplate = z
  .object({
    id: Uuid,
    salonId: Uuid,
    key: Slug,
    name: NonEmptyName,
    channel: CommsChannel,
    kind: CommsKind,
    subject: z.string().nullable(),
    body: z.string(),
    active: z.boolean(),
    createdAt: IsoDateTime,
  })
  .meta({ id: 'CommsTemplate' });
export type CommsTemplate = z.infer<typeof CommsTemplate>;

export const UpsertCommsTemplate = z
  .object({
    key: Slug,
    name: NonEmptyName,
    channel: CommsChannel.default('sms'),
    kind: CommsKind.default('reminder'),
    subject: z.string().max(200).nullish(),
    body: z.string().min(1).max(4000),
  })
  .meta({ id: 'UpsertCommsTemplate' });
export type UpsertCommsTemplate = z.infer<typeof UpsertCommsTemplate>;

// ── Query schemas (NO .meta — query/param gotcha) ───────────────────────────────
export const ListCommsMessagesQuery = z.object({
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).default(0),
  channel: CommsChannel.optional(),
  kind: CommsKind.optional(),
  clientId: Uuid.optional(),
  appointmentId: Uuid.optional(),
  campaignId: Uuid.optional(),
  from: IsoDateTime.optional(),
  to: IsoDateTime.optional(),
});
export type ListCommsMessagesQuery = z.infer<typeof ListCommsMessagesQuery>;
